import { Popover, Portal, useDisclosure } from '@chakra-ui/react';
import { IQuoteRespSchema } from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { isBefore } from 'date-fns/isBefore';
import { max } from 'date-fns/max';
import { parseISO } from 'date-fns/parseISO';
import { startOfToday } from 'date-fns/startOfToday';
import isEmpty from 'lodash/isEmpty';
import { useMemo, useState } from 'react';
import FocusLock from 'react-focus-lock';
import { useExtendExpirationDate } from '../../api/cpqService';
import { useQuoteContext } from '../../routes/Quotes/Quote/quoteContext';
import { useToast } from '../../services/toast';
import { getExpirationColors } from '../../utils/quotes';
import { MButton, MFlex, MFormField, MVStack } from '../Monetize';
import { DatePicker } from '../Monetize/DatePicker/DatePicker';
import { FOCUS_WITHOUT_SCROLLING } from '../Monetize/Overlays';

const QuoteExpirationDateSelector = ({
  quoteId,
  expirationDate,
  setQuote,
}: {
  quoteId: string;
  expirationDate: string;
  setQuote: (quote: IQuoteRespSchema | null) => void;
}) => {
  const { loadData } = useQuoteContext();
  const { onOpen, onClose, open } = useDisclosure();
  const [newExpirationDate, setNewExpirationDate] = useState(expirationDate);
  const { addToast } = useToast();

  // The backend rejects an extended date earlier than the current expiration
  // date, so neither the picker nor the Save button should offer one.
  const minDate = useMemo(
    () => max([startOfToday(), parseISO(expirationDate)]),
    [expirationDate],
  );

  const canSubmitDisabled =
    isEmpty(newExpirationDate) ||
    expirationDate === newExpirationDate ||
    isBefore(parseISO(newExpirationDate), minDate);

  const { mutate: updateExpirationDate, isPending: isLoading } =
    useExtendExpirationDate({
      meta: { showErrorToast: true },
      onSuccess: (quote) => {
        addToast({
          summary: 'Expiration Date Updated',
          detail: `The expiration date has been updated`,
          severity: 'success',
        });
        loadData();
        onClose();
        setQuote(quote);
      },
    });

  const handleExpirationDate = async () => {
    if (isEmpty(newExpirationDate)) {
      return;
    }

    updateExpirationDate({
      quoteId,
      payload: { extendedDate: newExpirationDate },
    });
  };

  return (
    <Popover.Root
      lazyMount
      unmountOnExit
      open={open}
      onOpenChange={(details) => {
        if (details.open) {
          setNewExpirationDate(expirationDate);
          onOpen();
        } else {
          onClose();
        }
      }}
      restoreFocus
      size="lg"
      positioning={{
        strategy: 'fixed',
        placement: 'bottom-end',
        offset: { mainAxis: 0, crossAxis: 0 },
      }}
    >
      <MFlex minH={8} align="center" mr={-3}>
        <Popover.Trigger asChild>
          <MButton
            color={getExpirationColors(expirationDate)}
            variant="tertiary"
            size="sm"
            my={2}
          >
            {toDateShort(expirationDate)}
          </MButton>
        </Popover.Trigger>
      </MFlex>

      <Portal>
        <Popover.Positioner>
          <Popover.Content
            data-testid="custom-expirationDate-popover-content"
            w="280px"
          >
            <Popover.Body>
              <FocusLock focusOptions={FOCUS_WITHOUT_SCROLLING}>
                <MVStack gap={2} alignItems={'flex-start'}>
                  <MFormField label="Expiration Date">
                    <DatePicker
                      value={expirationDate}
                      onChange={(date) => {
                        setNewExpirationDate(date ?? '');
                      }}
                      minDate={minDate}
                      readonlyContentProps={{ pb: 0, pt: 1.5, mr: 2 }}
                      btnProps={{ my: 0 }}
                    />
                  </MFormField>
                  <MFlex justifyContent="flex-end" w="100%">
                    <MButton
                      variant="primary"
                      isLoading={isLoading}
                      isDisabled={canSubmitDisabled}
                      onClick={handleExpirationDate}
                    >
                      Save
                    </MButton>
                  </MFlex>
                </MVStack>
              </FocusLock>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};

export default QuoteExpirationDateSelector;
